import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Bell, Receipt, ChevronRight, X, FileText, UtensilsCrossed,
  Clock, Phone, ClipboardList, CalendarDays, CheckCircle2,
} from "lucide-react";
import { CatalogoVisual } from "./menu-publico-catalogo";
import { aplicarFaviconPagina, aplicarTituloPagina } from "../lib/branding";

interface PdfEntry { nombre: string; url: string; descripcion?: string; }
interface MenuConfig {
  colorPrimario: string;
  colorSecundario: string;
  logoUrl: string | null;
  descripcionTienda: string | null;
  horarioAtencion: string | null;
  telefonoContacto: string | null;
  pdfs: PdfEntry[];
  mostrarLlamarMesonero?: boolean;
  mostrarSolicitarCuenta?: boolean;
}

export function MenuPublicoPage() {
  const { sucursalId, mesa: mesaParam } = useParams<{ sucursalId: string; mesa?: string }>();
  const sid = sucursalId ?? "1";
  const mesaFija = mesaParam ? Number(mesaParam) : null;

  const [config, setConfig] = useState<MenuConfig | null>(null);
  const [pdfAbierto, setPdfAbierto] = useState<PdfEntry | null>(null);
  const [vista, setVista] = useState<"inicio" | "pdf" | "catalogo">("inicio");
  const [tieneCatalogo, setTieneCatalogo] = useState<boolean | null>(null);
  const [modal, setModal] = useState<"llamar_mesonero" | "solicitar_cuenta" | null>(null);
  const [mesaInput, setMesaInput] = useState("");
  const [nombre, setNombre] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [error, setError] = useState("");

  // Detectar si hay catálogo configurado para esta sucursal
  useEffect(() => {
    fetch(`/api/v1/menu-publico/sucursal/${sid}/catalogo?modo=menu`)
      .then((r) => r.json())
      .then((d) => setTieneCatalogo(Array.isArray(d?.categorias) && d.categorias.length > 0))
      .catch(() => setTieneCatalogo(false));
  }, [sid]);

  useEffect(() => {
    fetch(`/api/v1/menu-publico/sucursal/${sid}/config`)
      .then((r) => r.json())
      .then((data) => {
        setConfig({
          colorPrimario: data.colorPrimario ?? "#1a1a1a",
          colorSecundario: data.colorSecundario ?? "#2980B9",
          logoUrl: data.logoUrl ?? null,
          descripcionTienda: data.descripcionTienda ?? null,
          horarioAtencion: data.horarioAtencion ?? null,
          telefonoContacto: data.telefonoContacto ?? null,
          pdfs: Array.isArray(data.pdfs) ? data.pdfs : [],
        });
        aplicarFaviconPagina(data.logoUrl);
        aplicarTituloPagina(data.sucursalNombre ?? data.descripcionTienda?.split(".")[0]?.trim());
      })
      .catch(() => {});
  }, [sid]);

  const enviar = async () => {
    const mesa = mesaFija ?? Number(mesaInput);
    if (!mesa || isNaN(mesa)) { setError("Ingresa el número de mesa."); return; }
    if (!modal) return;
    setEnviando(true); setError("");
    try {
      const endpoint = modal === "llamar_mesonero"
        ? "/api/v1/menu-publico/notificacion/llamar-mesonero"
        : "/api/v1/menu-publico/notificacion/solicitar-cuenta";
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sucursalId: Number(sid),
          numeroMesa: mesa,
          clienteNombre: nombre.trim() || undefined,
          sessionId: getSession(),
        }),
      });
      if (res.ok) {
        setEnviado(true);
        setTimeout(() => { setEnviado(false); setModal(null); setMesaInput(""); setNombre(""); }, 3000);
      } else { setError("Error al enviar. Intenta de nuevo."); }
    } catch { setError("Error de conexión."); }
    finally { setEnviando(false); }
  };

  const color = config?.colorPrimario ?? "#1a1a1a";
  const pdfs = config?.pdfs ?? [];

  // Patrón SVG botánico muy sutil como fondo
  const bgPattern = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'%3E%3Cg fill='none' stroke='%23e8e4dc' stroke-width='1' opacity='0.6'%3E%3Cellipse cx='60' cy='30' rx='8' ry='20' transform='rotate(-20 60 30)'/%3E%3Cellipse cx='30' cy='80' rx='6' ry='16' transform='rotate(30 30 80)'/%3E%3Cellipse cx='90' cy='90' rx='7' ry='18' transform='rotate(-15 90 90)'/%3E%3Cpath d='M60 50 Q70 40 75 30' stroke-width='0.8'/%3E%3Cpath d='M30 96 Q35 85 40 78' stroke-width='0.8'/%3E%3C/g%3E%3C/svg%3E")`;

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: "#fdfcfa", backgroundImage: bgPattern }}
    >
      {/* ── HEADER ── */}
      <div className="flex flex-col items-center pt-8 pb-4 px-4">
        {/* Logo circular */}
        {config?.logoUrl ? (
          <img
            src={config.logoUrl}
            alt="Logo"
            className="w-20 h-20 rounded-full object-cover border-2 border-white shadow-md mb-3"
          />
        ) : (
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-md mb-3"
            style={{ backgroundColor: color }}
          >
            {config?.descripcionTienda?.[0] ?? "M"}
          </div>
        )}

        {/* Nombre del restaurante */}
        <h1
          className="font-bold text-xl tracking-widest uppercase text-center"
          style={{ color: "#1a1a1a", fontFamily: "Georgia, 'Times New Roman', serif", letterSpacing: "0.15em" }}
        >
          {config?.descripcionTienda?.split(".")[0] ?? "Menú Digital"}
        </h1>

        {/* Descripción */}
        {config?.descripcionTienda && config.descripcionTienda.includes(".") && (
          <p className="text-sm text-gray-500 text-center mt-1 max-w-xs leading-relaxed">
            {config.descripcionTienda.split(".").slice(1).join(".").trim()}
          </p>
        )}

        {/* Info adicional */}
        {(config?.horarioAtencion || config?.telefonoContacto || mesaFija) && (
          <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
            {config?.horarioAtencion && (
              <span className="inline-flex items-center gap-1"><Clock size={12} aria-hidden /> {config.horarioAtencion}</span>
            )}
            {config?.telefonoContacto && (
              <span className="inline-flex items-center gap-1"><Phone size={12} aria-hidden /> {config.telefonoContacto}</span>
            )}
            {mesaFija && (
              <span
                className="px-2 py-0.5 rounded-full text-white font-semibold text-xs"
                style={{ backgroundColor: color }}
              >
                Mesa {mesaFija}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── INICIO: selector entre PDF y CATÁLOGO VISUAL ── */}
      {!pdfAbierto && vista === "inicio" && (
        <div className="px-4 pb-32 max-w-2xl mx-auto">
          {pdfs.length === 0 && !tieneCatalogo ? (
            <div className="text-center py-16 text-gray-400 text-sm">
              <span className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gray-100 text-gray-400 mb-3" aria-hidden>
                <ClipboardList size={28} strokeWidth={1.6} />
              </span>
              <p>El menú estará disponible pronto.</p>
            </div>
          ) : (
            <>
              {/* Cuando hay AMBOS: 2 botones grandes destacados */}
              {pdfs.length > 0 && tieneCatalogo && (
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <button
                    onClick={() => setVista("pdf")}
                    className="aspect-square flex flex-col items-center justify-center gap-3 bg-white rounded-2xl border-2 border-gray-100 hover:border-gray-300 hover:shadow-md transition p-4">
                    <FileText className="size-10" style={{ color }} />
                    <span className="font-bold uppercase tracking-wide text-gray-900 text-sm text-center" style={{ fontFamily: "Georgia, serif" }}>
                      Ver carta (PDF)
                    </span>
                    <span className="text-xs text-gray-400 text-center">
                      Formato completo
                    </span>
                  </button>
                  <button
                    onClick={() => setVista("catalogo")}
                    className="aspect-square flex flex-col items-center justify-center gap-3 rounded-2xl border-2 transition p-4 text-white shadow-md"
                    style={{ backgroundColor: color, borderColor: color }}>
                    <UtensilsCrossed className="size-10" />
                    <span className="font-bold uppercase tracking-wide text-sm text-center" style={{ fontFamily: "Georgia, serif" }}>
                      Explorar menú
                    </span>
                    <span className="text-xs opacity-80 text-center">
                      Con fotos y descripción
                    </span>
                  </button>
                </div>
              )}

              {/* Si solo hay catálogo, ir directo */}
              {pdfs.length === 0 && tieneCatalogo && (
                <CatalogoVisual sucursalId={Number(sid)} color={color} onVolver={() => setVista("inicio")} />
              )}

              {/* Lista de PDFs (cuando hay solo PDFs o además del selector) */}
              {pdfs.length > 0 && (
                <div className="space-y-3 mt-2">
                  {tieneCatalogo && (
                    <p className="text-xs text-gray-400 uppercase tracking-widest text-center mb-1">o ver carta tradicional</p>
                  )}
                  {pdfs.map((pdf, idx) => (
                    <button
                      key={idx}
                      onClick={() => setPdfAbierto(pdf)}
                      className="w-full flex items-center justify-between px-5 py-4 bg-white rounded-2xl shadow-sm border border-gray-100 hover:border-gray-300 hover:shadow-md transition-all text-left"
                    >
                      <div className="flex-1 min-w-0 pr-4">
                        <p className="font-bold uppercase tracking-wide text-gray-900 text-sm" style={{ fontFamily: "Georgia, serif" }}>
                          {pdf.nombre}
                        </p>
                        {pdf.descripcion && (
                          <p className="text-gray-400 text-xs mt-0.5 truncate">{pdf.descripcion}</p>
                        )}
                      </div>
                      <ChevronRight className="size-5 text-gray-400 shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── CATÁLOGO VISUAL ── */}
      {!pdfAbierto && vista === "catalogo" && (
        <CatalogoVisual sucursalId={Number(sid)} color={color} onVolver={() => setVista("inicio")} />
      )}

      {/* Vista PDF directo cuando solo hay PDF — el selector PDF está arriba */}
      {!pdfAbierto && vista === "pdf" && pdfs.length > 0 && (
        <div className="px-4 pb-32 max-w-2xl mx-auto">
          <button onClick={() => setVista("inicio")} className="text-sm text-gray-500 mb-3 flex items-center gap-1">
            ← Volver
          </button>
          <div className="space-y-3">
            {pdfs.map((pdf, idx) => (
              <button key={idx} onClick={() => setPdfAbierto(pdf)}
                className="w-full flex items-center justify-between px-5 py-4 bg-white rounded-2xl shadow-sm border border-gray-100 hover:border-gray-300 hover:shadow-md transition-all text-left">
                <div className="flex-1 min-w-0 pr-4">
                  <p className="font-bold uppercase tracking-wide text-gray-900 text-sm" style={{ fontFamily: "Georgia, serif" }}>
                    {pdf.nombre}
                  </p>
                  {pdf.descripcion && (
                    <p className="text-gray-400 text-xs mt-0.5 truncate">{pdf.descripcion}</p>
                  )}
                </div>
                <ChevronRight className="size-5 text-gray-400 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── VISOR PDF ── */}
      {pdfAbierto && (
        <div className="pb-28">
          {/* Barra de navegación del PDF */}
          <div className="sticky top-0 z-30 bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3 shadow-sm">
            <button
              onClick={() => setPdfAbierto(null)}
              className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="size-5 text-gray-500" />
            </button>
            <p className="font-bold uppercase tracking-wide text-gray-900 text-sm flex-1" style={{ fontFamily: "Georgia, serif" }}>
              {pdfAbierto.nombre}
            </p>
            {/* Pestañas de otras cartas */}
            {pdfs.length > 1 && (
              <div className="flex gap-1.5 overflow-x-auto">
                {pdfs.map((pdf, idx) => (
                  <button
                    key={idx}
                    onClick={() => setPdfAbierto(pdf)}
                    className={`shrink-0 px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                      pdfAbierto.url === pdf.url
                        ? "text-white border-transparent"
                        : "bg-white text-gray-600 border-gray-300 hover:border-gray-500"
                    }`}
                    style={pdfAbierto.url === pdf.url ? { backgroundColor: color, borderColor: color } : {}}
                  >
                    {pdf.nombre}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* PDF iframe */}
          <iframe
            src={`${pdfAbierto.url}#toolbar=0&navpanes=0&scrollbar=1`}
            className="w-full"
            style={{ height: "calc(100vh - 120px)", border: "none" }}
            title={pdfAbierto.nombre}
          />
        </div>
      )}

      {/* ── BOTONES FLOTANTES ── */}
      {/* Botón de reservar (esquina inferior izquierda) */}
      <div className="fixed bottom-6 left-4 z-50">
        <a href={`/reservar?sucursal=${sid}`}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-white text-sm font-medium shadow-lg transition-transform hover:scale-105 active:scale-95"
          style={{ backgroundColor: "rgba(30,30,30,0.78)" }}>
          <CalendarDays size={16} aria-hidden />
          <span>Reservar mesa</span>
        </a>
      </div>

      <div className="fixed bottom-6 right-4 flex flex-col gap-3 z-50">
        {/* Botón "Pedir cuenta" — oculto si la sucursal lo desactivó */}
        {(config?.mostrarSolicitarCuenta !== false) && (
        <button
          onClick={() => setModal("solicitar_cuenta")}
          className="flex items-center gap-2 pl-4 pr-5 py-3 rounded-full text-white font-semibold shadow-xl text-sm transition-transform hover:scale-105 active:scale-95"
          style={{ backgroundColor: config?.colorSecundario ?? "#2980B9" }}
        >
          <Receipt className="size-4" />
          <span>Pedir cuenta</span>
        </button>
        )}
        {/* Botón "Llamar mesonero" — oculto si la sucursal lo desactivó */}
        {(config?.mostrarLlamarMesonero !== false) && (
        <button
          onClick={() => setModal("llamar_mesonero")}
          className="flex items-center gap-2 pl-4 pr-5 py-3 rounded-full text-white font-semibold shadow-xl text-sm transition-transform hover:scale-105 active:scale-95"
          style={{ backgroundColor: color }}
        >
          <Bell className="size-4" />
          <span>Llamar mesonero</span>
        </button>
        )}
      </div>

      {/* ── MODAL NOTIFICACIÓN ── */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden">
            {enviado ? (
              <div className="text-center py-10 px-6">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-50 text-emerald-500 mb-4" aria-hidden>
                  <CheckCircle2 size={48} strokeWidth={1.8} />
                </div>
                <h3 className="font-bold text-xl text-gray-900">¡Listo!</h3>
                <p className="text-gray-500 text-sm mt-2">
                  {modal === "llamar_mesonero"
                    ? "El mesonero estará contigo en breve."
                    : "Tu cuenta está en camino."}
                </p>
              </div>
            ) : (
              <>
                {/* Header del modal */}
                <div
                  className="px-6 py-5 text-white text-center"
                  style={{ backgroundColor: modal === "llamar_mesonero" ? color : (config?.colorSecundario ?? "#2980B9") }}
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/20 mb-1" aria-hidden>
                    {modal === "llamar_mesonero"
                      ? <Bell size={28} strokeWidth={1.8} />
                      : <Receipt size={28} strokeWidth={1.8} />}
                  </div>
                  <h3 className="font-bold text-lg">
                    {modal === "llamar_mesonero" ? "Llamar mesonero" : "Solicitar cuenta"}
                  </h3>
                  {mesaFija && (
                    <p className="text-sm opacity-80 mt-0.5">Mesa {mesaFija}</p>
                  )}
                </div>

                <div className="px-6 py-5 space-y-4">
                  {/* Mesa (solo si no viene en URL) */}
                  {!mesaFija && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Número de mesa
                      </label>
                      <input
                        type="number"
                        value={mesaInput}
                        onChange={(e) => { setMesaInput(e.target.value); setError(""); }}
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-center text-3xl font-bold text-gray-900 focus:outline-none focus:border-gray-900 transition-colors"
                        placeholder="0"
                        autoFocus
                      />
                    </div>
                  )}

                  {/* Nombre */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Tu nombre <span className="normal-case font-normal">(opcional)</span>
                    </label>
                    <input
                      type="text"
                      value={nombre}
                      onChange={(e) => setNombre(e.target.value)}
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-gray-900 focus:outline-none focus:border-gray-900 transition-colors"
                      placeholder="Ej: María"
                    />
                  </div>

                  {error && (
                    <p className="text-red-500 text-sm text-center">{error}</p>
                  )}

                  {/* Botones */}
                  <div className="flex gap-3 pt-1">
                    <button
                      onClick={() => { setModal(null); setMesaInput(""); setNombre(""); setError(""); }}
                      className="flex-1 py-3 rounded-xl border-2 border-gray-200 text-gray-600 font-semibold hover:border-gray-300 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={enviar}
                      disabled={enviando}
                      className="flex-1 py-3 rounded-xl text-white font-semibold transition-all hover:opacity-90 active:scale-95 disabled:opacity-60"
                      style={{ backgroundColor: modal === "llamar_mesonero" ? color : (config?.colorSecundario ?? "#2980B9") }}
                    >
                      {enviando ? "Enviando..." : "Enviar aviso"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function getSession(): string {
  let id = localStorage.getItem("menu_session");
  if (!id) { id = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("menu_session", id); }
  return id;
}
